import React, { useState } from 'react';
import { useTheme } from './ThemeContext';

// ── Normalized data types ─────────────────────────────────────────────────────

export interface COOEvidence {
  source: string;
  country: string | null;
  // Swarm-native fields (post-2026-05-22 — sourced from
  // swarm_scored_products.factor_details). One evidence row = one
  // (source × factor) pair, so a single source typically produces 3-5
  // evidence entries.
  factor?: string | null;          // factor name, e.g. "labeler_country_matches_coo"
  hit?: boolean | null;            // did this factor's condition fire?
  category?: string | null;        // "regulatory" / "trade_flow" / etc.
  result?: string | null;          // human-readable explanation written by the scorer
  rawInputs?: Record<string, any> | null;  // inputs the scorer fed into this factor
  // Legacy fields preserved for transitional rendering only. Will be
  // dropped once all consumers have migrated to the Swarm shape.
  confidence?: string;
  score?: number | null;
  matchText?: string | null;
  validationNote?: string | null;
  deductionReasoning?: string | null;
}

export interface COOScoring {
  baseline: number | null;
  baselineReason: string | null;
  rawScore: number | null;
  categoriesCount: number | null;
  adjustments: Array<{ weight: number; reason: string; source?: string; key?: string }>;
  ceilingsApplied: Array<{ ceiling: number; rule: string }>;
  contradictions: string[];
}

export interface COOScoreCardProps {
  country: string | null;
  confidence: string | null;
  finalScore: number | null;
  tier: string | null;
  productName?: string | null;
  scoring: COOScoring | null;
  evidences: COOEvidence[];
  primaryDeduction: string | null;
  note?: string | null;
  // When true, hide the Made-in-USA score + Score Breakdown chain (stale calc),
  // keeping only the COO country + per-source evidence list.
  hideScore?: boolean;
}

// ── Source icons ──────────────────────────────────────────────────────────────
const SOURCE_ICON: Record<string, string> = {
  DailyMed_XML:      "📄",
  FDA_Label_API:     "🏛",
  FDA_Establishments:"🏭",
  Vision_API:        "📦",
  Package_OCR:       "📦",
  OpenFDA_NDC:       "🔬",
  SEC_EDGAR:         "🔍",
  Trade_Import:      "🚢",
  EPA_TRI:           "🏭",
  FDA_Shortage:      "⚠️",
  FDA_Recall:        "🚨",
};

const CONFIDENCE_DOT: Record<string, string> = {
  high:     "#22C55E",
  medium:   "#F59E0B",
  low:      "#94A3B8",
  very_low: "#CBD5E1",
};

// Plain language based on Made in USA % (input: raw finalScore where 0=US, 100=foreign)
function madeInUsaPlainLanguage(finalScore: number | null): string {
  if (finalScore === null) return "";
  const s = 100 - finalScore;
  if (s >= 90) return "Almost certainly US-manufactured";
  if (s >= 70) return "Likely US-manufactured";
  if (s >= 50) return "Possibly US-manufactured";
  if (s >= 30) return "Possibly foreign-manufactured";
  if (s >= 10) return "Likely foreign-manufactured";
  return "Almost certainly foreign-manufactured";
}

// Human-readable ceiling explanations
function ceilingLabel(rule: string, ceiling: number, categoriesCount: number | null, contradictionsCount: number): string {
  switch (rule) {
    case "triangulation": {
      const count = categoriesCount ?? 0;
      const needed = Math.max(1, 4 - count);
      const catWord = count > 1 ? "categories" : "category";
      const moreWord = needed === 1 ? "1 more independent category" : `${needed} more independent categories`;
      return `Only ${count > 0 ? count : "?"} source ${catWord} — score capped at ${ceiling} pts (${moreWord} needed to reach 100%)`;
    }
    case "unresolved_contradiction":
      return `${contradictionsCount} unresolved contradiction${contradictionsCount > 1 ? "s" : ""} — score capped at ${ceiling} pts`;
    case "puerto_rico":
      return `Puerto Rico facility detected — US territory ceiling at ${ceiling} pts`;
    case "insufficient_evidence":
      return `Insufficient evidence — score capped at ${ceiling} pts`;
    default:
      return `${rule}: max ${ceiling} pts`;
  }
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function COOScoreCard({
  country,
  confidence,
  finalScore,
  tier,
  productName,
  scoring,
  evidences,
  primaryDeduction,
  note,
  hideScore = false,
}: COOScoreCardProps) {
  const { T } = useTheme();
  const [evExpanded, setEvExpanded] = useState<Record<string, boolean>>({});
  // Hidden in UI for now: detailed per-evidence deduction is already displayed.
  const showPrimaryDeduction = false;

  const sc = scoring;
  const adjustments = sc?.adjustments ?? [];
  const ceilings = sc?.ceilingsApplied ?? [];
  const contradictions = sc?.contradictions ?? [];
  const pointsBySource = adjustments.reduce((acc: Record<string, number>, adj) => {
    const source = (adj?.source || '').toString();
    if (!source) return acc;
    acc[source] = (acc[source] || 0) + (adj.weight || 0);
    return acc;
  }, {});

  const countryColor = country === "United States" ? T.success
    : country === "China" ? T.danger
    : country ? "#D97706"
    : T.textLight;

  const confidenceColor = confidence === "high" ? T.success
    : confidence === "medium" ? "#D97706"
    : T.textLight;

  const tierColor = tier === "HIGH" ? T.danger
    : tier === "MEDIUM" ? "#D97706"
    : tier === "LOW" ? T.success
    : T.textLight;

  const scoreColor = finalScore !== null
    ? (finalScore >= 70 ? T.danger : finalScore >= 50 ? "#D97706" : T.success)
    : T.textLight;

  const sourcesWithCountry = evidences.filter(e => e.country).length;

  // Map each evidence source to its root site domain.
  // Multiple scrapers hitting the same site count as one distinct source.
  const SOURCE_SITE: Record<string, string> = {
    DailyMed_XML:       'DailyMed',
    Vision_API:         'DailyMed',
    Haiku_SPL:          'DailyMed',
    FDA_Label_API:      'FDA',
    FDA_Establishments: 'FDA',
    OpenFDA_NDC:        'FDA',
    Trade_Import:       'FDA',
    Labeler_Hint:       'Heuristic',
    SEC_EDGAR:          'SEC EDGAR',
    EPA_TRI:            'EPA',
    FDA_Shortage:       'FDA',
    FDA_Recall:         'FDA',
  };
  const distinctSites = new Set(
    evidences.map(e => SOURCE_SITE[e.source] ?? e.source)
  ).size;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, fontSize: 12 }}>

      {/* ── Hero: country + score + tier ── */}
      <div style={{
        display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", gap: 12,
        padding: "14px 16px",
        background: "linear-gradient(135deg, #F8FAFC 0%, #EFF6FF 100%)",
        borderRadius: T.radiusSm, border: `1px solid ${T.border}`
      }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>
            Country of Origin
          </div>
          <div style={{ fontSize: 20, fontWeight: 800, color: countryColor, lineHeight: 1 }}>
            {country || "Not identified"}
          </div>
          <div style={{ marginTop: 6, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <span
              style={{ fontSize: 11, color: T.textLight, cursor: "help", borderBottom: `1px dotted ${T.border}` }}
              title={`${distinctSites} distinct data site${distinctSites !== 1 ? 's' : ''} queried (DailyMed, FDA, etc.). A single site may contribute multiple signals — this number reflects unique source sites, not individual data points.`}
            >
              <strong style={{ color: T.navy }}>{distinctSites}</strong> distinct site{distinctSites !== 1 ? 's' : ''}
            </span>
            {productName && (
              <>
                <span style={{ color: T.border }}>·</span>
                <span style={{ fontSize: 11, color: T.textLight }}>{productName}</span>
              </>
            )}
          </div>
        </div>
        {!hideScore && (
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 3 }}>
            Made in USA
          </div>
          <div style={{ fontSize: 32, fontWeight: 900, color: scoreColor, lineHeight: 1 }}>
            {finalScore !== null ? `${100 - finalScore}%` : "—"}
          </div>
          {finalScore !== null && (
            <div style={{ fontSize: 10, color: scoreColor, fontWeight: 600, marginTop: 3, lineHeight: 1.3 }}>
              {madeInUsaPlainLanguage(finalScore)}
            </div>
          )}
          {tier && (
            <div style={{
              marginTop: 5, display: "inline-block", padding: "2px 10px", borderRadius: 20,
              background: tierColor + "18", border: `1px solid ${tierColor}40`,
              fontSize: 10, fontWeight: 700, color: tierColor,
              textTransform: "uppercase", letterSpacing: "0.06em"
            }}>
              {tier}
            </div>
          )}
        </div>
        )}
      </div>

      {/* ── Scoring chain ── */}
      {sc && !hideScore && (
        <div style={{ padding: "12px 14px", background: "#F8FAFC", borderRadius: T.radiusSm, border: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
            Score Breakdown
          </div>

          {/* Baseline */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 8, paddingBottom: 8, borderBottom: `1px dashed ${T.border}` }}>
            <div style={{ minWidth: 52, textAlign: "right", fontWeight: 800, fontSize: 15, color: T.navy }}>
              {sc.baseline ?? "—"}<span style={{ fontSize: 10, fontWeight: 400, color: T.textLight }}> pts</span>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 11, color: T.textDark, marginBottom: 2 }}>Baseline (Spec 4.1)</div>
              {sc.baselineReason && (
                <div style={{ fontSize: 11, color: T.textMid, lineHeight: 1.45 }}>{sc.baselineReason}</div>
              )}
            </div>
          </div>

          {/* Adjustments */}
          {adjustments.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 10, color: T.textLight, fontWeight: 600, marginBottom: 6 }}>Adjustments</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {adjustments.map((adj, idx) => {
                  const w = adj.weight ?? 0;
                  // weight > 0 increases foreign risk → Malus for Made in USA
                  // weight < 0 decreases foreign risk → Bonus for Made in USA
                  const isMalus = w > 0;
                  const label = isMalus ? "MALUS" : "BONUS";
                  const color = isMalus ? T.danger : T.success;
                  return (
                    <div key={idx} style={{ display: "flex", gap: 8, alignItems: "baseline", fontSize: 11 }}>
                      <span style={{ fontSize: 9, fontWeight: 800, color, textTransform: "uppercase", letterSpacing: "0.05em", minWidth: 36 }}>
                        {label}
                      </span>
                      <span style={{ minWidth: 28, textAlign: "right", fontWeight: 700, color }}>
                        {w > 0 ? "+" : ""}{w}
                      </span>
                      <span style={{ color: T.textMid, lineHeight: 1.4, flex: 1 }}>
                        {adj.reason || adj.key}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Raw → Final chain */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", paddingTop: 6, borderTop: `1px dashed ${T.border}` }}>
            <span
              title="Raw score (pts) = baseline + all adjustments, before any ceiling is applied. Ceilings limit the max pts when evidence is incomplete or contradictory."
              style={{ fontSize: 11, color: T.textLight, cursor: "help", borderBottom: "1px dashed #CBD5E1" }}
            >
              Raw:
            </span>
            <strong style={{ fontSize: 13, color: T.navy }}>{sc.rawScore ?? "—"} pts</strong>
            {ceilings.map((c, idx) => (
              <React.Fragment key={idx}>
                <span style={{ fontSize: 11, color: T.textLight }}>→ capped at</span>
                <strong style={{ fontSize: 13, color: "#D97706" }}>{c.ceiling} pts</strong>
              </React.Fragment>
            ))}
            <span style={{ fontSize: 11, color: T.textLight }}>→ Made in USA:</span>
            <strong style={{ fontSize: 13, color: scoreColor }}>
              {finalScore !== null ? `${100 - finalScore}%` : "—"}
            </strong>
          </div>

          {/* Ceiling explanations */}
          {ceilings.length > 0 && (
            <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
              {ceilings.map((c, idx) => (
                <div key={idx} style={{ display: "flex", gap: 6, alignItems: "flex-start", padding: "6px 8px", background: "#FFFBEB", borderRadius: 4, border: "1px solid #FDE68A" }}>
                  <span style={{ fontSize: 12 }}>⚠️</span>
                  <span style={{ fontSize: 11, color: "#92400E", lineHeight: 1.4 }}>
                    {ceilingLabel(c.rule, c.ceiling, sc.categoriesCount, contradictions.length)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Contradictions */}
          {contradictions.length > 0 && (
            <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}>
              {contradictions.map((c, idx) => (
                <div key={idx} style={{ display: "flex", gap: 6, alignItems: "flex-start", padding: "6px 8px", background: "#FEF2F2", borderRadius: 4, border: "1px solid #FECACA" }}>
                  <span style={{ fontSize: 12 }}>⚡</span>
                  <span style={{ fontSize: 11, color: T.danger, lineHeight: 1.4 }}>{c}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Evidence sources (Swarm factor_details, grouped by source) ── */}
      {evidences.length > 0 && (() => {
        // Group factors by source so each source becomes one collapsible
        // section. This matches the Swarm shape (one source emits 3-5
        // factor rows) — flat lists with 60+ rows are unreadable.
        type Group = { source: string; factors: COOEvidence[]; hits: number };
        const grouped: Group[] = [];
        const idxBySource = new Map<string, number>();
        for (const ev of evidences) {
          const src = ev.source || "Unknown";
          let i = idxBySource.get(src);
          if (i === undefined) {
            i = grouped.length;
            idxBySource.set(src, i);
            grouped.push({ source: src, factors: [], hits: 0 });
          }
          grouped[i].factors.push(ev);
          if (ev.hit) grouped[i].hits += 1;
        }
        // Sort: sources with hits first, then by name.
        grouped.sort((a, b) => (b.hits - a.hits) || a.source.localeCompare(b.source));

        return (
        <div style={{ padding: "12px 14px", background: T.offWhite, borderRadius: T.radiusSm, border: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
            Evidence Sources <span style={{ fontWeight: 400, textTransform: "none" }}>({grouped.length} sources · {evidences.length} factors)</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {grouped.map(({ source, factors, hits }) => {
              const icon = SOURCE_ICON[source] || "🔍";
              const key = `src-${source}`;
              const isOpen = !!evExpanded[key];
              const hasDerivedScore = Object.prototype.hasOwnProperty.call(pointsBySource, source);
              const displayScore = hasDerivedScore ? pointsBySource[source] : null;
              const scorePrefix = (displayScore ?? 0) >= 0 ? "+" : "";
              // Surface any non-null country among this source's factors
              const sourceCountry = factors.map(f => f.country).find(c => c) || null;
              const hitRatioColor = hits === factors.length ? T.success
                : hits === 0 ? "#CBD5E1"
                : "#D97706";
              return (
                <div key={source} style={{ background: T.white, borderRadius: 4, border: `1px solid ${T.border}`, overflow: "hidden" }}>
                  {/* Source header row — click to expand factors */}
                  <button
                    onClick={() => setEvExpanded(prev => ({ ...prev, [key]: !prev[key] }))}
                    style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: "none", border: "none", cursor: "pointer", textAlign: "left" }}
                  >
                    <span style={{ fontSize: 11, transform: isOpen ? "rotate(90deg)" : "rotate(0)", display: "inline-block", transition: "transform 0.15s", color: T.textLight }}>▶</span>
                    <span style={{ fontSize: 13 }}>{icon}</span>
                    <span style={{ fontWeight: 700, fontSize: 11, color: T.textDark, flex: 1 }}>{source}</span>
                    {hits > 0 && sourceCountry && (
                      <span style={{ fontSize: 11, fontWeight: 600, color: sourceCountry === "United States" ? T.success : "#D97706" }}>{sourceCountry}</span>
                    )}
                    <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: T.textLight }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: hitRatioColor, display: "inline-block" }} />
                      {hits}/{factors.length} hit
                    </span>
                    {displayScore !== null && displayScore !== undefined && displayScore !== 0 && (
                      <span style={{ fontSize: 10, color: T.textLight, minWidth: 36, textAlign: "right" }}>{scorePrefix}{displayScore}pts</span>
                    )}
                  </button>

                  {/* Factor list — collapsed by default */}
                  {isOpen && (
                    <div style={{ borderTop: `1px solid ${T.border}`, padding: "6px 10px", display: "flex", flexDirection: "column", gap: 6, background: "#FBFBFA" }}>
                      {factors.map((f, fIdx) => {
                        const hitMark = f.hit === true ? "✓" : f.hit === false ? "✗" : "·";
                        const hitColor = f.hit === true ? T.success : f.hit === false ? T.textLight : T.textMid;
                        const factorKey = `${key}-f-${fIdx}`;
                        const factorOpen = !!evExpanded[factorKey];
                        const hasRawInputs = f.rawInputs && Object.keys(f.rawInputs).length > 0;
                        return (
                          <div key={fIdx} style={{ paddingLeft: 6, borderLeft: `2px solid ${hitColor}` }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: T.textDark }}>
                              <span style={{ fontWeight: 700, color: hitColor, minWidth: 12, textAlign: "center" }}>{hitMark}</span>
                              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: T.textMid, flex: 1 }}>
                                {f.factor || "(no factor name)"}
                              </span>
                              {f.category && (
                                <span style={{ fontSize: 9, fontWeight: 600, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.05em" }}>{f.category}</span>
                              )}
                              {f.country && (
                                <span style={{ fontSize: 10, fontWeight: 600, color: f.country === "United States" ? T.success : "#D97706" }}>{f.country}</span>
                              )}
                            </div>
                            {f.result && (
                              <div style={{ padding: "3px 0 0 20px", fontSize: 10, color: T.textMid, lineHeight: 1.45 }}>
                                {f.result}
                              </div>
                            )}
                            {hasRawInputs && (
                              <div style={{ padding: "2px 0 0 20px" }}>
                                <button
                                  onClick={() => setEvExpanded(prev => ({ ...prev, [factorKey]: !prev[factorKey] }))}
                                  style={{ padding: 0, background: "none", border: "none", cursor: "pointer", fontSize: 9, color: T.textLight, display: "flex", alignItems: "center", gap: 4 }}
                                >
                                  <span style={{ transform: factorOpen ? "rotate(90deg)" : "rotate(0)", display: "inline-block", transition: "transform 0.15s" }}>▶</span>
                                  Raw inputs
                                </button>
                                {factorOpen && (
                                  <pre style={{ margin: "4px 0 0 0", padding: "6px 8px", background: "#F8FAFC", borderRadius: 3, fontSize: 9, color: T.textMid, lineHeight: 1.45, whiteSpace: "pre-wrap", fontFamily: "'IBM Plex Mono', monospace", overflowX: "auto" }}>
                                    {JSON.stringify(f.rawInputs, null, 2)}
                                  </pre>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        );
      })()}

      {/* ── Primary deduction (main source, only shown when available) ── */}
      {showPrimaryDeduction && primaryDeduction && (
        <div style={{ padding: "10px 14px", background: T.white, borderRadius: T.radiusSm, border: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
            Primary Deduction Path
          </div>
          <div style={{ fontSize: 10, color: T.textMid, lineHeight: 1.55, whiteSpace: "pre-wrap", fontFamily: "'IBM Plex Mono', monospace" }}>
            {primaryDeduction}
          </div>
        </div>
      )}

    </div>
  );
}
